import {
  Badge,
  BadgeProps,
  Link,
  PropertyFilterProps,
  TableProps,
} from "@amzn/awsui-components-console";
import {
  DealStatus,
  FixedFeeConfig,
  FixedFeeType,
  SimplifiedFixedFeeConfig,
} from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import { TokenValue } from "aws-sdk/clients/ssm";
import { DateForm } from "src/components/allFixedFeeConfigsPage/components/date-form";
import Decimal from "decimal.js";
import React, { ReactElement } from "react";
import { LinkProps } from "@amzn/awsui-components-console/link";

interface BadgeInfoType {
  content: string;
  uniqueValue?: string; // used for filtering, defaults to content if not provided
  color: BadgeProps["color"];
  rank: number;
}

export const getFixedFeeDealStatus = (
  fixedfeeConfig: SimplifiedFixedFeeConfig | FixedFeeConfig,
): BadgeInfoType => {
  switch (fixedfeeConfig.dealStatus) {
    case DealStatus.DELETED:
      return {
        content: "DELETED",
        color: "severity-neutral",
        rank: -2,
      };
    case DealStatus.TERMINATED:
      return {
        content: "TERMINATED",
        color: "red",
        rank: -1,
      };
    case DealStatus.ENABLED:
      if (fixedfeeConfig.effectiveTerminationDate) {
        return {
          content: `ENABLED (PENDING TERMINATION)`,
          uniqueValue: "ENABLED",
          color: "severity-low",
          rank: 2,
        };
      }
      return {
        content: "ENABLED",
        uniqueValue: "ENABLED",
        color: "green",
        rank: 1,
      };
  }

  return {
    content: "DRAFT",
    color: "grey",
    rank: 0,
  };
};

export const getFixedFeeDealDateStatus = (
  fixedFeeConfig: SimplifiedFixedFeeConfig | FixedFeeConfig,
): BadgeInfoType => {
  const currentDate = new Date().toISOString().split("T")[0];

  if (currentDate < fixedFeeConfig.dealStartDate!) {
    return {
      content: "FUTURE",
      color: "blue",
      rank: 1,
    };
  } else if (
    currentDate > fixedFeeConfig.dealEndDate! ||
    (fixedFeeConfig.effectiveTerminationDate &&
      currentDate > fixedFeeConfig.effectiveTerminationDate)
  ) {
    return {
      content: "PAST",
      color: "grey",
      rank: -1,
    };
  } else {
    return {
      content: "CURRENT",
      color: "green",
      rank: 0,
    };
  }
};

export const getFixedFeeType = (
  fixedFeeConfig: SimplifiedFixedFeeConfig | FixedFeeConfig,
): BadgeInfoType => {
  if (fixedFeeConfig.fixedFeeType === FixedFeeType.TERMINATION_AT_CONVENIENCE) {
    return {
      content: "TAC",
      color: "blue",
      rank: 1,
    };
  } else {
    return {
      content: "STANDARD",
      color: "green",
      rank: 0,
    };
  }
};

export const FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS: {
  [id: string]: {
    label: string;
    hidden?: boolean;
    minWidth?: number;
    maxWidth?: number;
    stringCell?: (item: SimplifiedFixedFeeConfig) => string | undefined;
    linkCell?: (
      item: SimplifiedFixedFeeConfig,
      domain: string,
    ) => ReactElement<LinkProps> | undefined;
    dateCell?: (item: SimplifiedFixedFeeConfig) => string | undefined;
    badgeCell?: (item: SimplifiedFixedFeeConfig) => BadgeInfoType | undefined;
    numberCell?: (item: SimplifiedFixedFeeConfig) => NumericValue | undefined;
    arrayCell?: (item: SimplifiedFixedFeeConfig) => string[] | undefined;
    sortingField?: string; // for custom attribute
    sortingComparator?: (
      a: SimplifiedFixedFeeConfig,
      b: SimplifiedFixedFeeConfig,
    ) => number; // for nested/custom attribute
  };
} = {
  fixedFeeId: {
    label: "FixedFee ID",
    minWidth: 220,
    linkCell: (item: SimplifiedFixedFeeConfig) => (
      <Link
        external
        href={`/getfixedfeeconfig/${item.fixedFeeId}`}
        ariaLabel={item.fixedFeeId}
      >
        {item.fixedFeeId}
      </Link>
    ),
  },
  environment: {
    label: "Environment",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.environment,
  },
  dealStatus: {
    label: "Deal Status",
    minWidth: 260,
    badgeCell: (item: SimplifiedFixedFeeConfig | FixedFeeConfig) =>
      getFixedFeeDealStatus(item),
    sortingComparator: (a, b) =>
      getFixedFeeDealStatus(a).rank - getFixedFeeDealStatus(b).rank,
  },
  dealDateStatus: {
    label: "Deal Date Status",
    minWidth: 120,
    badgeCell: (item: SimplifiedFixedFeeConfig) =>
      getFixedFeeDealDateStatus(item),
    sortingComparator: (a, b) =>
      getFixedFeeDealDateStatus(a).rank - getFixedFeeDealDateStatus(b).rank,
  },
  fixedFeeType: {
    label: "FixedFee Type",
    minWidth: 120,
    badgeCell: (item: SimplifiedFixedFeeConfig) => getFixedFeeType(item),
    sortingComparator: (a, b) =>
      getFixedFeeType(a).rank - getFixedFeeType(b).rank,
  },
  dealStartDate: {
    label: "Deal Start Date",
    minWidth: 120,
    dateCell: (item: SimplifiedFixedFeeConfig) => item.dealStartDate,
  },
  dealEndDate: {
    label: "Deal End Date",
    minWidth: 120,
    dateCell: (item: SimplifiedFixedFeeConfig) => item.dealEndDate,
  },
  effectiveTerminationDate: {
    label: "Effective Termination Date",
    minWidth: 200,
    dateCell: (item: SimplifiedFixedFeeConfig) => item.effectiveTerminationDate,
  },
  resellerIds: {
    label: "Reseller IDs",
    minWidth: 120,
    arrayCell: (item: SimplifiedFixedFeeConfig) => item.dealConfig?.resellerIds,
    sortingComparator: (a, b) =>
      (a.dealConfig?.resellerIds?.toString() ?? "").localeCompare(
        b.dealConfig?.resellerIds?.toString() ?? "",
      ),
  },
  fusePlanIds: {
    label: "Fuse Plan IDs",
    minWidth: 380,
    maxWidth: 400,
    arrayCell: (item: SimplifiedFixedFeeConfig) => item.dealConfig?.fusePlanIds,
    sortingComparator: (a, b) =>
      (a.dealConfig?.fusePlanIds?.toString() ?? "").localeCompare(
        b.dealConfig?.fusePlanIds?.toString() ?? "",
      ),
  },
  serviceProvider: {
    label: "Service Provider",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.serviceProvider,
  },
  benefitPeriodDuration: {
    label: "Benefit Period Duration",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.benefitPeriodDuration,
  },
  contractPrice: {
    label: "Contract Price",
    minWidth: 120,
    numberCell: (item: SimplifiedFixedFeeConfig) => item.contractPrice,
  },
  initialPlanPrice: {
    label: "Initial Plan Price",
    minWidth: 120,
    numberCell: (item: SimplifiedFixedFeeConfig) => item.initialPlanPrice,
  },
  contractCurrencyCode: {
    label: "Currency Code",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.contractCurrencyCode,
  },
  invoiceFrequency: {
    label: "Invoice Frequency",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) => item.invoiceFrequency,
  },
  marketplaceId: {
    label: "Marketplace ID",
    minWidth: 120,
    stringCell: (item: SimplifiedFixedFeeConfig) =>
      item.dealConfig?.marketplaceId,
    sortingComparator: (a, b) =>
      (a.dealConfig?.marketplaceId?.toString() ?? "").localeCompare(
        b.dealConfig?.marketplaceId?.toString() ?? "",
      ),
  },
  accountingRegistrationId: {
    label: "Accounting Registration ID",
    minWidth: 220,
    stringCell: (item: SimplifiedFixedFeeConfig) =>
      item.accountingRegistrationId,
  },
};

export const FIXED_FEE_CONFIG_ATTRIBUTES: string[] = Object.keys(
  FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS,
);
export const FIXED_FEE_CONFIG_FILTERS: PropertyFilterProps.FilteringProperty[] =
  FIXED_FEE_CONFIG_ATTRIBUTES.map((attr) => {
    const {
      label,
      stringCell,
      linkCell,
      dateCell,
      badgeCell,
      numberCell,
      arrayCell,
    } = FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attr];

    let defaultOperator = undefined;
    let operators: (
      | PropertyFilterProps.ComparisonOperator
      | PropertyFilterProps.ExtendedOperator<TokenValue>
    )[] = [];

    if (stringCell || linkCell) {
      defaultOperator = "=";
      operators = ["=", "!="];
    } else if (dateCell) {
      defaultOperator = ">=";
      operators = ["=", "!=", "<", "<=", ">", ">="].map((operator) => ({
        operator,
        form: DateForm,
        format: (date: string) => date,
        match: "datetime" as const,
      }));
    } else if (badgeCell) {
      defaultOperator = "=";
      operators = ["=", "!="];
    } else if (numberCell) {
      defaultOperator = "=";
      operators = ["=", "!=", "<", "<=", ">", ">="];
    } else if (arrayCell) {
      defaultOperator = ":";
      operators = [":", "!:"];
    }

    return {
      key: attr,
      defaultOperator,
      operators: operators,
      propertyLabel: label,
      groupValuesLabel: label + " values",
    };
  });

export const STRING_OR_DATE_CELL_WRAPPER = (item?: string): string =>
  item ?? "";

export const BADGE_CELL_WRAPPER = (item?: BadgeInfoType): ReactElement => {
  if (!item) return <></>;
  return <Badge color={item.color}>{item.content}</Badge>;
};

export const LINK_CELL_WRAPPER = (item?: ReactElement<LinkProps>): string =>
  item?.props?.ariaLabel ?? "";
export const NUMBER_CELL_WRAPPER = (item?: NumericValue | Decimal): string =>
  item
    ? Number(
        item instanceof NumericValue ? item.string : item.toString(),
      ).toLocaleString("en-US", {
        maximumFractionDigits: 4,
      })
    : "";
export const ARRAY_CELL_WRAPPER = (item?: string[]): ReactElement => {
  if (!item) return <></>;

  return (
    <p>
      {item.map((val, index) => (
        <React.Fragment key={index}>
          {index > 0 ? (
            <>
              ,<br />
              {val}
            </>
          ) : (
            val
          )}
        </React.Fragment>
      ))}
    </p>
  );
};

export const getFixedFeeConfigTableColDefs = (
  domain: string,
): TableProps.ColumnDefinition<SimplifiedFixedFeeConfig>[] =>
  FIXED_FEE_CONFIG_ATTRIBUTES.map((attr) => {
    const {
      label,
      hidden,
      minWidth,
      maxWidth,
      stringCell,
      dateCell,
      badgeCell,
      linkCell,
      numberCell,
      arrayCell,
      sortingField,
      sortingComparator,
    } = FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attr];

    let cellWrapper: { (item: SimplifiedFixedFeeConfig): ReactElement };
    if (stringCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) => (
        <span>{STRING_OR_DATE_CELL_WRAPPER(stringCell(item))}</span>
      );
    } else if (dateCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) => (
        <span>{STRING_OR_DATE_CELL_WRAPPER(dateCell(item))}</span>
      );
    } else if (badgeCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) =>
        BADGE_CELL_WRAPPER(badgeCell(item));
    } else if (linkCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) =>
        linkCell(item, domain) ?? <></>;
    } else if (numberCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) => (
        <span>{NUMBER_CELL_WRAPPER(numberCell(item))}</span>
      );
    } else if (arrayCell) {
      cellWrapper = (item: SimplifiedFixedFeeConfig) =>
        ARRAY_CELL_WRAPPER(arrayCell(item));
    }

    return {
      id: attr,
      header: label,
      cell: cellWrapper!,
      hidden,
      minWidth,
      maxWidth,
      sortingField: sortingField ?? attr,
      sortingComparator,
    };
  });
